import { IServiceProps } from './types'
import './Services.scss'
import { useCallback, useState } from 'react'
import classNames from 'classnames'
import { __ } from '@wordpress/i18n'
import arrowDownIcon from '../../../../public/images/arrow-down.svg'
import clockIcon from '../../../../public/images/icon-clock.svg'
import cartReduceIcon from '../../../../public/images/icon-cart-reduce.svg'
import cartIncreaseIcon from '../../../../public/images/icon-cart-increase.svg'
import { useBookingContext } from '../../providers/BookingFormProvider/BookingFormProvider'
import { wbkFormatPrice } from '../../providers/BookingFormProvider/utils'
import { Button } from '../Button/Button'
import { minutesToText } from '../../../admin/components/WebbaDataTable/utils'
import checkIcon from '../../../../public/images/icon-checkmark.svg'
import { useWording } from '../../hooks/useWording'

export const ServiceItem = ({
    id,
    label,
    description,
    duration,
    price,
    image,
    quantity,
    min_quantity,
    max_quantity,
    selected,
    onUpdate,
    group_booking,
}: IServiceProps) => {
    const { priceFormat } = useBookingContext()
    const wording = useWording()
    const [descriptionExpanded, setDescriptionExpanded] = useState(false)

    const setQuantity = useCallback(
        (updatedQuantity: number) => {
            if (
                (min_quantity && updatedQuantity < min_quantity) ||
                (max_quantity && updatedQuantity > max_quantity)
            )
                return

            onUpdate({
                quantity: updatedQuantity,
            })
        },
        [quantity, max_quantity, min_quantity, onUpdate]
    )

    const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        // Only allow numbers, and clamp to min/max
        let num = parseInt(value, 10)
        if (isNaN(num)) num = min_quantity || 1
        if (min_quantity && num < min_quantity) num = min_quantity
        if (max_quantity && num > max_quantity) num = max_quantity
        setQuantity(num)
    }

    const toggleSelectService = useCallback(() => {
        onUpdate({
            selected: !selected,
        })
    }, [id, selected, onUpdate])

    return (
        <div
            className={classNames('wbk_service_item', {
                ['wbk_service_item--selected']: selected,
            })}
            onClick={toggleSelectService}
        >
            <div className={'wbk_service_item__top-part'}>
                {image && (
                    <img
                        src={image}
                        alt={label}
                        className={'wbk_service_item__image'}
                    />
                )}
                <div className={'wbk_service_item__content-wrapper'}>
                    <div className={'wbk_service_item__heading-wrapper'}>
                        <div className={'wbk_service_item__heading-content'}>
                            <h3>{label}</h3>
                            <p
                                className={
                                    'wbk_service_item__heading-wrapper__duration'
                                }
                            >
                                <img
                                    src={clockIcon}
                                    alt={
                                        wording.duration ||
                                        __('Duration', 'webba-booking-lite')
                                    }
                                />
                                <span>
                                    {minutesToText(Number(duration), {
                                        h: wording.hour,
                                        min: wording.minute,
                                    })}
                                </span>
                            </p>
                        </div>
                        <div
                            className={
                                'wbk_service_item__heading-wrapper__price'
                            }
                        >
                            {(price && Number(price) > 0 && (
                                <span>
                                    {wbkFormatPrice(Number(price), priceFormat)}
                                </span>
                            )) ||
                                wording.free ||
                                __('Free', 'webba-booking-lite')}
                        </div>
                    </div>
                    {description && (
                        <p
                            className={classNames(
                                'wbk_service_item__description',
                                {
                                    ['wbk_service_item__description--expanded']:
                                        descriptionExpanded,
                                }
                            )}
                            dangerouslySetInnerHTML={{
                                __html:
                                    (description.length > 190 &&
                                        !descriptionExpanded &&
                                        description.slice(0, 190)) ||
                                    description,
                            }}
                        />
                    )}
                    {description && description.length > 190 && (
                        <>
                            <div
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setDescriptionExpanded(!descriptionExpanded)
                                }}
                                className={classNames(
                                    'wbk_service_item__description-toggle',
                                    {
                                        ['wbk_service_item__description-toggle--expanded']:
                                            descriptionExpanded,
                                    }
                                )}
                            >
                                <strong>
                                    {(descriptionExpanded &&
                                        (wording.show_less ||
                                            __(
                                                'Show less',
                                                'webba-booking-lite'
                                            ))) ||
                                        wording.show_more ||
                                        __('Show more', 'webba-booking-lite')}
                                </strong>
                                <img
                                    src={arrowDownIcon}
                                    alt={
                                        wording.toggle_description ||
                                        __(
                                            'Toggle description',
                                            'webba-booking-lite'
                                        )
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            {max_quantity && max_quantity > 1 && group_booking && (
                <div className={'wbk_service_item__bottom-part'}>
                    <div className={'wbk_service_item__bottom-part__heading'}>
                        <p
                            className={
                                'wbk_service_item__bottom-part__heading__title'
                            }
                        >
                            {wording.for_group_bookings_only ||
                                __(
                                    'For group bookings only:',
                                    'webba-booking-lite'
                                )}
                        </p>
                        <span>
                            {wording.number_of_people ||
                                __('Number of people:', 'webba-booking-lite')}
                        </span>
                    </div>
                    <div
                        className={
                            'wbk_service_item__bottom-part__quantity-wrapper'
                        }
                    >
                        <div
                            className={classNames(
                                'wbk_service_item__bottom-part__quantity-wrapper__button',
                                'wbk_service_item__bottom-part__quantity-wrapper__button--reduce'
                            )}
                            onClick={(e: any) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setQuantity((quantity || 1) - 1)
                            }}
                        >
                            <img
                                src={cartReduceIcon}
                                alt={
                                    wording.reduce_item ||
                                    __('Reduce item', 'webba-booking-lite')
                                }
                            />
                        </div>
                        <input
                            type="number"
                            value={quantity || 1}
                            min={min_quantity || 1}
                            max={max_quantity || 1}
                            onChange={handleQuantityInputChange}
                            className={
                                'wbk_service_item__bottom-part__quantity-wrapper__input'
                            }
                            onClick={(e: any) => {
                                e.preventDefault()
                                e.stopPropagation()
                            }}
                        />
                        <div
                            className={classNames(
                                'wbk_service_item__bottom-part__quantity-wrapper__button',
                                'wbk_service_item__bottom-part__quantity-wrapper__button--increase'
                            )}
                            onClick={(e: any) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setQuantity(Number(quantity) + 1)
                            }}
                        >
                            <img
                                src={cartIncreaseIcon}
                                alt={
                                    wording.increase_item ||
                                    __('Increase item', 'webba-booking-lite')
                                }
                            />
                        </div>
                    </div>
                </div>
            )}
            <Button
                classes={classNames('wbk_service_item__mobile-select-button', {
                    ['wbk_service_item__mobile-select-button--selected']: selected,
                })}
                type={selected ? 'secondary' : 'primary'}
                icon={(selected && checkIcon) || ''}
            >
                {selected
                    ? wording.selected || __('Selected', 'webba-booking-lite')
                    : wording.select || __('+ Select', 'webba-booking-lite')}
            </Button>
        </div>
    )
}
